import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button, Text } from '@mercai/clever'

import { Tabs, TabsItemProps } from '@/components'
import { ROUTES_ADMIN } from '@/router'

import { TabGeneratePurchaseOrder } from './tab-generate-purchase-order'
import { TabInventory } from './tab-inventory'
import { TabPurchaseOrder } from './tab-purchase-order'
import { TabShipmentInvoice } from './tab-shipment-invoice'
import { TabSupplierTransaction } from './tab-supplier-transaction'
import { TabSupplyPaymentRequired } from './tab-supply-payment-required'

import {
  getActualTab,
  tabPath,
  TabsPathsEnum,
} from './supplier-order-management.constants'

import styles from './supplier-order-management.module.css'

export const SupplierOrderManagementPage = () => {
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [isOpenNewShipmentInvoice, setIsOpenNewShipmentInvoice] =
    useState(false)

  const location = useLocation()
  const navigate = useNavigate()

  const actualTab = useMemo(
    () => getActualTab(location.pathname),
    [location.pathname],
  )

  const checkActive = (path: string) => location.pathname === `/${path}`

  const onSelectTab = (path: string) => {
    navigate(`/${path}`)
  }

  const tabsItems: TabsItemProps[] = [
    {
      title: t('tabTitleGeneratePurchaseOrder'),
      onSelect: () => onSelectTab(tabPath('generate-purchase-order')),
      isActive: checkActive(tabPath('generate-purchase-order')),
    },
    {
      title: t('tabTitlePurchaseOrder'),
      onSelect: () => onSelectTab(tabPath('purchase-order')),
      isActive: checkActive(tabPath('purchase-order')),
    },
    {
      title: t('tabTitleSupplyPaymentRequired'),
      onSelect: () => onSelectTab(tabPath('supply-payment-required')),
      isActive: checkActive(tabPath('supply-payment-required')),
    },
    {
      title: t('tabTitleSupplierTransaction'),
      onSelect: () => onSelectTab(tabPath('supplier-transaction')),
      isActive: checkActive(tabPath('supplier-transaction')),
    },
    {
      title: t('tabTitleShipmentInvoice'),
      onSelect: () => onSelectTab(tabPath('shipment-invoice')),
      isActive: checkActive(tabPath('shipment-invoice')),
    },
    {
      title: t('tabTitleInventory'),
      onSelect: () => onSelectTab(tabPath('inventory')),
      isActive: checkActive(tabPath('inventory')),
    },
  ]

  const tabTables: Record<TabsPathsEnum, React.ReactNode> = {
    'generate-purchase-order': <TabGeneratePurchaseOrder />,
    'purchase-order': <TabPurchaseOrder />,
    'supply-payment-required': <TabSupplyPaymentRequired />,
    'supplier-transaction': <TabSupplierTransaction />,
    'shipment-invoice': (
      <TabShipmentInvoice
        isOpenNewShipmentInvoice={isOpenNewShipmentInvoice}
        onHandleOpenNewShipmentInvoice={() =>
          setIsOpenNewShipmentInvoice(false)
        }
      />
    ),
    inventory: <TabInventory />,
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles['header-content']}>
          <div>
            <Text variant="title-large">{t('title')}</Text>
            <div>
              <Text variant="text-base" className={styles['sub-title']}>
                {t('description')}
              </Text>
            </div>
          </div>
          {actualTab === 'generate-purchase-order' && (
            <Button
              href={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.GENERATE_PURCHASE_ORDER.CREATE.fullPath()}
              model="pill"
            >
              {t('tableExpandedComponentGeneratePurchaseOrderNewButton')}
            </Button>
          )}
          {actualTab === 'shipment-invoice' && (
            <Button
              onClick={() => setIsOpenNewShipmentInvoice(true)}
              model="pill"
            >
              {t('tabShipmentInvoiceRequestNewShipmentInvoiceButton')}
            </Button>
          )}
        </div>
      </header>

      <Tabs items={tabsItems} />

      {tabTables[actualTab as TabsPathsEnum]}
    </div>
  )
}
